/**
 * Hook para gerenciar preferências de som do usuário
 * Persiste via useUserPreferences (por usuário)
 */

import { useCallback, useMemo } from 'react';
import { useUserPreferences } from './useUserPreferences';
import type { SoundEventType } from './useNotificationSound';

export interface SoundPreferences {
  enabled: boolean;
  volume: number; // 0.0 a 1.0
  enabledEvents: Record<SoundEventType, boolean>;
}

const DEFAULT_PREFERENCES: SoundPreferences = {
  enabled: true,
  volume: 0.7,
  enabledEvents: {
    new_order: true,
    urgent: true,
    delayed: true,
    completed: false, // Desativado por padrão (pode ser muito frequente)
    ready_pickup: true,
    logistics_alert: true,
    chat_message: true,
    // ⚠️ Ligados por padrão: no posto de bipagem o som É o retorno. O operador
    // olha para a pilha, não para a tela — sem ele, cada leitura custa uma
    // conferência visual. Quem não usa o posto nunca vai ouvi-los.
    scan_ok: true,
    scan_error: true,
    error: true
  }
};

export const useSoundPreferences = () => {
  const { getPreference, setPreference } = useUserPreferences({ module: 'global' });
  
  // Ler preferências do storage, merge com defaults
  const preferences = useMemo(() => {
    const saved = getPreference<Partial<SoundPreferences>>('sound', {});
    return {
      ...DEFAULT_PREFERENCES,
      ...saved,
      enabledEvents: {
        ...DEFAULT_PREFERENCES.enabledEvents,
        ...(saved.enabledEvents || {})
      }
    };
  }, [getPreference]);

  // Salvar preferências
  const savePreferences = useCallback((newPrefs: SoundPreferences) => {
    setPreference('sound', newPrefs);
  }, [setPreference]);

  // Atualizar preferências parcialmente
  const setPreferences = useCallback((newPrefs: Partial<SoundPreferences>) => {
    const updated = {
      ...preferences,
      ...newPrefs,
      enabledEvents: {
        ...preferences.enabledEvents,
        ...(newPrefs.enabledEvents || {})
      }
    };
    savePreferences(updated);
  }, [preferences, savePreferences]);

  // Toggle som globalmente
  const toggleEnabled = useCallback(() => {
    savePreferences({
      ...preferences,
      enabled: !preferences.enabled
    });
  }, [preferences, savePreferences]);

  // Ajustar volume
  const setVolume = useCallback((volume: number) => {
    savePreferences({
      ...preferences,
      volume: Math.max(0, Math.min(1, volume))
    });
  }, [preferences, savePreferences]);

  // Toggle evento específico
  const toggleEvent = useCallback((event: SoundEventType) => {
    savePreferences({
      ...preferences,
      enabledEvents: {
        ...preferences.enabledEvents,
        [event]: !preferences.enabledEvents[event]
      }
    });
  }, [preferences, savePreferences]);

  // Resetar para padrões
  const resetToDefaults = useCallback(() => {
    savePreferences(DEFAULT_PREFERENCES);
  }, [savePreferences]);

  return {
    preferences,
    setPreferences,
    toggleEnabled,
    setVolume,
    toggleEvent,
    resetToDefaults
  };
};
